import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { User } from "../../types/User";
import api from "../../utils/api";

interface PaginatedUsers {
  users: User[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

type AssignRoleResult = {
  user_id: number;
  status: string;
  operation:
    | "role_update"
    | "partner_update"
    | "smile_update"
    | "cases_update"
    | "photo_recolour_update"
    | "surgery_booking_update";
  role?: User["role"];
  partner?: User["partner"];
  verifySmile?: boolean;
  verified?: boolean;
  verifyPhotoRecolour?: boolean;
  verifySurgeryBooking?: boolean;
  photoRecolourCredits?: number;
};

interface AdminState {
  isAuth: boolean;
  isLoading: boolean;
  verifiedUsers: PaginatedUsers;
  error?: string;
}

const initialState: AdminState = {
  isAuth: false,
  isLoading: false,
  verifiedUsers: {
    users: [],
    totalCount: 0,
    currentPage: 1,
    totalPages: 1,
  },
  error: undefined,
};

export const getVerifiedUsers = createAsyncThunk<
  PaginatedUsers,
  {
    page?: number;
    limit?: number;
    search?: string;
    portalAccess?: "" | "smile" | "cases";
  }
>(
  "admin/getVerifiedUsers",
  async ({ page = 1, limit = 50, search = "", portalAccess = "" }) => {
  const response = await api.get(
    `/auth/admin/users`,
    {
      params: {
        page,
        limit,
        search,
        ...(portalAccess ? { portalAccess } : {}),
      },
    },
  );

  return {
    users: response.data.data.users,
    totalCount: response.data.data.totalCount,
    currentPage: response.data.data.currentPage,
    totalPages: response.data.data.totalPages,
  };
  }
);

export const assignUserRoles = createAsyncThunk<
  { results: AssignRoleResult[] },
  {
    updateData: {
      user_id: number;
      role?: string;
      updatedPartner?: string;
      verifySmile?: boolean;
      verified?: boolean;
      verifyPhotoRecolour?: boolean;
      verifySurgeryBooking?: boolean;
      photoRecolourCredits?: number;
    }[];
  }
>("admin/assignUserRoles", async ({ updateData }) => {
  const response = await api.put(
    "/auth/admin/assign-role",
    { updateData },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
});

const AdminRoleControlsSlice = createSlice({
  name: "adminRoleControls",
  initialState,
  reducers: {
    resetVerifiedUsersState: (state) => {
      state.verifiedUsers = {
        users: [],
        totalCount: 0,
        currentPage: 1,
        totalPages: 1,
      };
      state.isLoading = false;
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getVerifiedUsers.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(getVerifiedUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.verifiedUsers = action.payload;
      })
      .addCase(getVerifiedUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.verifiedUsers = {
          users: [],
          totalCount: 0,
          currentPage: 1,
          totalPages: 1,
        };
        state.error = action.error.message;
      })

      // assign user role 
      .addCase(assignUserRoles.fulfilled, (state, action) => {
        state.isLoading = false;

        for (const result of action.payload.results) {
          if (result.status !== "success") continue;

          const user = state.verifiedUsers.users.find(
            (u) => u.user_id === result.user_id
          );

          if (!user) continue;

          switch (result.operation) {
            case "role_update":
              user.role = result.role!;
              break;

            case "partner_update":
              user.partner = result.partner!;
              break;

            case "smile_update":
              user.verifySmile = result.verifySmile!;
              break;

            case "cases_update":
              user.verified = result.verified!;
              break;

            case "photo_recolour_update":
              user.verifyPhotoRecolour = result.verifyPhotoRecolour!;
              break;

            case "surgery_booking_update":
              user.verifySurgeryBooking = result.verifySurgeryBooking!;
              break;
          }
        }
      });
  },
});

export const { resetVerifiedUsersState } = AdminRoleControlsSlice.actions;

export default AdminRoleControlsSlice.reducer;
